import { DestroyRef, Injectable, signal } from '@angular/core';
import { getAuth } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { dataStore, db, storage } from '../app.component';
import { AddressData } from './address-validation-flow.service';

const maxEmployeePhotoSize = 5 * 1024 * 1024;

const auditExcludedFields = new Set([
  'addressCoordinates',
  'addressCoordinatesFor',
  'addressFingerprint',
  'location',
]);

export interface EmployeeContact extends AddressData {
  phone: string;
}
export interface EmployeeProfileResult {
  changed: boolean;
  photoUrl?: string | null;
  auditLogged?: boolean;
}

@Injectable({ providedIn: 'root' })
export class EmployeeProfileService {
  private readonly database = db;
  private readonly storage = storage;

  async saveContact(
    organisationId: string,
    contact: EmployeeContact,
  ): Promise<EmployeeProfileResult> {
    const employeeId = getAuth().currentUser?.uid;
    if (!employeeId) throw new Error('U bent niet ingelogd.');
    return this.updateProfile(organisationId, employeeId, {
      phone: contact.phone.trim(),
      postal: String(contact.postal ?? '')
        .replace(/\s/g, '')
        .toUpperCase(),
      houseNumber: contact.houseNumber ?? '',
      houseNumberAddition: contact.houseNumberAddition?.trim() ?? '',
      streetName: contact.streetName?.trim() ?? '',
      city: contact.city?.trim() ?? '',
      ...(contact.addressCoordinates !== undefined
        ? { addressCoordinates: contact.addressCoordinates }
        : {}),
      ...(contact.addressCoordinatesFor !== undefined
        ? { addressCoordinatesFor: contact.addressCoordinatesFor }
        : {}),
      ...(contact.addressFingerprint !== undefined
        ? { addressFingerprint: contact.addressFingerprint }
        : {}),
      ...(contact.location !== undefined ? { location: contact.location } : {}),
    });
  }

  async savePhoto(
    organisationId: string,
    employeeId: string,
    file: File | null,
    admin = false,
  ): Promise<EmployeeProfileResult> {
    const currentUser = getAuth().currentUser;
    if (!currentUser || (!admin && employeeId !== currentUser.uid)) {
      throw new Error('U kunt alleen uw eigen profielfoto wijzigen.');
    }
    const userRef = doc(this.database, `organisations/${organisationId}/users/${employeeId}`);
    const snapshot = await getDoc(userRef);
    if (!snapshot.exists()) throw new Error('Medewerker niet gevonden.');
    const before = snapshot.data();
    let photoUrl: string | null = null;
    const photoRef = file
      ? ref(
          this.storage,
          `organisations/${organisationId}/employee_photos/${employeeId}.${crypto.randomUUID().replace(/-/g, '')}`,
        )
      : null;
    try {
      if (file && photoRef) {
        const error = employeePhotoError(file);
        if (error) throw new Error(error);
        const uploaded = await uploadBytes(photoRef, file, { contentType: file.type });
        photoUrl = await getDownloadURL(uploaded.ref);
      }
      const result = await this.updateProfile(organisationId, employeeId, { photoUrl }, before);
      if (before.photoUrl && before.photoUrl !== photoUrl) {
        try {
          const oldRef = ref(this.storage, before.photoUrl);
          if (
            oldRef.fullPath.startsWith(
              `organisations/${organisationId}/employee_photos/${employeeId}.`,
            )
          ) {
            await deleteObject(oldRef);
          }
        } catch (error) {
          console.warn('Previous employee photo could not be removed.', error);
        }
      }
      return { ...result, photoUrl };
    } catch (error) {
      if (photoRef) {
        try {
          await deleteObject(photoRef);
        } catch (cleanupError) {
          console.warn('Uncommitted employee photo could not be removed.', cleanupError);
        }
      }
      throw error;
    }
  }

  private async updateProfile(
    organisationId: string,
    employeeId: string,
    patch: Record<string, any>,
    previous?: Record<string, unknown>,
  ): Promise<EmployeeProfileResult> {
    const actor = getAuth().currentUser;
    if (!actor) throw new Error('U bent niet ingelogd.');
    const actorName = dataStore.orgUser?.name ?? dataStore.rootUser?.name ?? '';
    const actorRights =
      dataStore.rootUser?.rights === 'admin'
        ? 'superuser'
        : (dataStore.orgUser?.roles ?? []).join(',');
    const userRef = doc(this.database, `organisations/${organisationId}/users/${employeeId}`);
    let before = previous;
    if (!before) {
      const snapshot = await getDoc(userRef);
      if (!snapshot.exists()) throw new Error('Medewerker niet gevonden.');
      before = snapshot.data();
    }
    const changes = Object.entries(patch).flatMap(([field, value]) => {
      if (auditExcludedFields.has(field)) return [];
      const from = before[field] ?? null;
      return from === value ? [] : [{ field, from, to: value }];
    });
    if (!changes.length) return { changed: false };
    await updateDoc(userRef, patch);
    try {
      await addDoc(collection(this.database, `eventLogs/${organisationId}/changedUserSettings`), {
        date: serverTimestamp(),
        doneByUser: actor.email ?? 'unknown',
        doneByUid: actor.uid,
        doneByName: actorName,
        doneWithRights: actorRights,
        doneToUid: employeeId,
        doneToName: before.name ?? '',
        changes,
      });
      return { changed: true, auditLogged: true };
    } catch (error) {
      console.warn('Employee profile saved, but its change log could not be written.', error);
      return { changed: true, auditLogged: false };
    }
  }
}

export function getInitial(name: string | null | undefined): string {
  return name?.trim().charAt(0).toUpperCase() || '?';
}

export function createEmployeePhotoDraft(destroyRef: DestroyRef) {
  const selectedPhoto = signal<File | null>(null);
  const photoPreviewUrl = signal<string | null>(null);
  const photoRemoved = signal(false);
  const photoValidationError = signal('');
  let objectUrl: string | null = null;
  let savedPhotoUrl: string | null = null;
  const release = () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  };
  destroyRef.onDestroy(release);
  return {
    selectedPhoto,
    photoPreviewUrl,
    photoRemoved,
    photoValidationError,
    select: (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      const error = employeePhotoError(file);
      photoValidationError.set(error);
      input.value = '';
      if (error) return;
      release();
      selectedPhoto.set(file);
      photoRemoved.set(false);
      objectUrl = URL.createObjectURL(file);
      photoPreviewUrl.set(objectUrl);
    },
    remove: () => {
      selectedPhoto.set(null);
      photoRemoved.set(!!savedPhotoUrl);
      photoValidationError.set('');
      release();
      photoPreviewUrl.set(null);
    },
    reset: (url: string | null = null) => {
      release();
      selectedPhoto.set(null);
      savedPhotoUrl = url;
      photoPreviewUrl.set(url);
      photoRemoved.set(false);
      photoValidationError.set('');
    },
  };
}

function employeePhotoError(file: File): string {
  if (!file.type.startsWith('image/')) return 'Selecteer een afbeeldingsbestand.';
  if (file.size > maxEmployeePhotoSize) return 'Dit bestand mag niet groter zijn dan 5 MB.';
  if (!file.size) return 'Selecteer een niet-lege afbeelding.';
  return '';
}
